import React, { ChangeEvent, FC, useRef, useState } from "react";

import { useLayout } from "../../hooks/use-layout";

import { Button, ButtonVariant, Dialog, InputField, Theme } from "@in/component-library";
import useSWR, { mutate } from "swr";
import { getUserAccounts } from "../../../beta/apis/getUserAccounts";
import { verifyEmail } from "../../apis/verify-email";
import { VerifyEmailResponse } from "../../models/verify-email-response";
import VerifyEmailSucceeded from "../verify-email-succeeded/verify-email-succeeded";

interface VerifyEmailProps {
  displayVerifyEmailModal: boolean;
  resendCode: () => void;
  updateEmail: () => void;
  onClose: (refreshCode: void) => void;
  email: string;
}

const VerifyEmail: FC<VerifyEmailProps> = ({ displayVerifyEmailModal, resendCode, updateEmail, onClose, email }) => {
  const { translations } = useLayout();

  const [succeeded, setSucceeded] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [displayConfirmationModal, setConfirmationModal] = useState<boolean>(false);
  const [displayResendCode, setDisplayResendCode] = useState<boolean>(false);
  const [codeInput, setCodeInput] = useState<string>("");
  const { mutate: mutateUserAccounts } = useSWR(`userAccounts`, getUserAccounts);

  const inputRef = useRef<HTMLInputElement>(null);
  const useDialogRef = useRef<HTMLDialogElement>(null);

  if (displayVerifyEmailModal) {
    useDialogRef?.current?.showModal();
  } else {
    useDialogRef?.current?.close();
  }

  const handleResendCode = () => {
    resendCode();
    setDisplayResendCode(true);
    refreshCode();
  };

  const handleSuccess = (r: VerifyEmailResponse) => {
    if (r.isVerified) {
      mutate("accounts/selected", r.account);
      mutateUserAccounts();
      updateEmail();

      setSucceeded(true);
      setErrorMessage("");
      setConfirmationModal(true);
    } else {
      setErrorMessage(translations.verifyEmailFailedText);
      inputRef.current?.focus();
    }
  };

  const handleInvalidCode = () => {
    setErrorMessage(translations.verifyEmailFailedText);
    inputRef.current?.focus();
  };

  const handleNotFound = () => {
    setErrorMessage(translations.verifyEmailCodeNotFoundText);
  };

  const submit = async (code: string) => {
    setIsSaving(true);
    setErrorMessage("");

    try {
      const result = await verifyEmail(code);

      switch (result.code) {
        case 200:
          handleSuccess(result?.body);
          break;
        case 400:
          handleInvalidCode();
          break;
        case 404:
          handleNotFound();
          break;
      }
    } finally {
      setIsSaving(false);
    }
  };

  const closeConfirmationModal = () => {
    setConfirmationModal(false);
    refreshCode();
    setSucceeded(false);
    onClose();
    setDisplayResendCode(false);
  };

  const refreshCode = () => {
    setCodeInput("");
    setErrorMessage("");
  };

  return (
    <>
      {!succeeded && (
        <Dialog
          ref={useDialogRef}
          className="email__modal"
          title={translations.verifyEmailHeader}
          onClose={closeConfirmationModal}
        >
          <div aria-live="polite">
            <p className="email__verification-text">
              {displayResendCode
                ? translations.verifyEmailSentNewVerificationText
                : translations.verifyEmailSentVerificationText}{" "}
              <span className="email__new-email">{email}</span>
            </p>
          </div>

          <div className="email__input-field">
            <InputField
              className={errorMessage && "email__error"}
              type="text"
              name={"emailVerification"}
              value={codeInput}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setCodeInput((e.target as HTMLInputElement).value)}
              errorMsg={errorMessage}
              label={translations.verifyEmailCodeLabel}
              ref={inputRef}
            />
          </div>

          <div className="email__resend-code">
            <p>{translations.verifyEmailNoCodeLabel}</p>
            <Button
              variant={ButtonVariant.Link}
              theme={Theme.Neutral}
              onClick={() => {
                handleResendCode();
                inputRef.current?.focus();
              }}
            >
              {translations.verifyEmailResendCode}
            </Button>
          </div>

          <div aria-live="polite" className="email__button">
            <Button
              aria-label={isSaving ? translations.verifyEmailCheckingCode : translations.verifyEmailCheckCodeLabel}
              type="submit"
              id="confirmButton"
              variant={ButtonVariant.Solid}
              theme={Theme.Neutral}
              onClick={() => submit(codeInput)}
              disabled={isSaving}
              isLoading={isSaving}
            >
              {translations.verifyEmailCheckCodeLabel}
            </Button>

            <Button
              type="button"
              variant={ButtonVariant.Outlined}
              theme={Theme.Neutral}
              onClick={closeConfirmationModal}
            >
              {translations.cancelBtnLabel}
            </Button>
          </div>
        </Dialog>
      )}

      <VerifyEmailSucceeded displayConfirmationModal={displayConfirmationModal} onClose={closeConfirmationModal} />
    </>
  );
};

export default VerifyEmail;
